import React, { useMemo, useRef, useState } from 'react';
import { AlertTriangleIcon, BanknoteIcon, CheckCircle2Icon, Loader2Icon, MapPinIcon, MessageCircleIcon, PackageIcon, TruckIcon } from 'lucide-react';
import { cn, formatCurrency } from '../../../lib/utils';
import { assignExpressDelivery, unassignExpressDelivery, setExpressDeliveryPaid, type ExpressAssignmentRow, type ExpressCourier } from '../../../lib/express-hooks';
import { buildCourierWhatsappHref } from '../../../lib/express-whatsapp';

// ──────────────────────────────────────────────────────────────────
// Asignación de entregas a repartidores.
//
// La lista se arma por TIPO DE ENTREGA (a domicilio), no por tarifa: un
// envío de ₡0 también se asigna y cuenta como entrega del repartidor.
//
// Al asignar, el sistema toma la ubicación y el monto del propio pedido
// — no hay nada que digitar. La operación pasa por una RPC atómica que
// guarda un snapshot inmutable y evita duplicados si dos personas
// asignan el mismo pedido al mismo tiempo.
// ──────────────────────────────────────────────────────────────────

interface ExpressAssignSectionProps {
  restaurantId: number | string;
  rows: ExpressAssignmentRow[];
  activeCouriers: ExpressCourier[];
  loading: boolean;
  /**
   * Solo administración ve dinero. Con `false` se ocultan los montos a
   * pagar y el marcado de pago; la asignación de repartidor y los datos
   * operativos (pedido, hora, dirección) se mantienen intactos.
   */
  canSeeMoney: boolean;
  onChanged: () => void;
}
const fmtTimeCR = (iso: string) => {
  try {
    return new Date(iso).toLocaleString('es-CR', {
      timeZone: 'America/Costa_Rica',
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch {
    return iso;
  }
};
export function ExpressAssignSection({
  restaurantId,
  rows,
  activeCouriers,
  loading,
  canSeeMoney,
  onChanged
}: ExpressAssignSectionProps) {
  const [busyOrderId, setBusyOrderId] = useState<number | null>(null);
  const [busyDeliveryId, setBusyDeliveryId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Una llave por pedido, reutilizada en reintentos para que un doble
  // clic o una red intermitente no generen dos asignaciones.
  const keysRef = useRef<Map<number, string>>(new Map());
  const pendingCount = useMemo(() => rows.filter(r => !r.delivery).length, [rows]);
  const pendingPayout = useMemo(() => rows.reduce((sum, r) => r.delivery && !r.delivery.paid_at ? sum + r.delivery.payout_amount : sum, 0), [rows]);
  const handleAssign = async (row: ExpressAssignmentRow, courierId: number) => {
    if (!courierId) return;
    const orderId = row.order.id;
    // Reasignar una entrega ya pagada mueve dinero de un repartidor a
    // otro en el reporte: se confirma antes.
    if (row.delivery?.paid_at) {
      const ok = window.confirm(`Esta entrega ya está marcada como pagada a ${row.delivery.courier_name}.\n\n` + 'Reasignarla mueve ese monto al nuevo repartidor en el reporte. ¿Continuar?');
      if (!ok) return;
    }
    if (!keysRef.current.has(orderId)) {
      keysRef.current.set(orderId, crypto.randomUUID());
    }
    setBusyOrderId(orderId);
    setError(null);
    setNotice(null);
    const result = await assignExpressDelivery({
      restaurantId,
      orderId,
      courierId,
      idempotencyKey: keysRef.current.get(orderId) as string
    });
    setBusyOrderId(null);
    if (!result.success) {
      setError(result.error || 'No se pudo asignar la entrega.');
      if (result.conflict) {
        keysRef.current.delete(orderId);
        onChanged();
      }
      return;
    }
    keysRef.current.delete(orderId);
    onChanged();
  };

  /** Un valor vacío en el selector significa "quitar el repartidor". */
  const handleCourierChange = (row: ExpressAssignmentRow, value: string) => {
    if (!value) {
      handleUnassign(row);
      return;
    }
    handleAssign(row, Number(value));
  };
  const handleUnassign = async (row: ExpressAssignmentRow) => {
    const {
      delivery,
      order
    } = row;
    if (!delivery) return;
    const ok = window.confirm(delivery.paid_at ? `Esta entrega ya está marcada como pagada a ${delivery.courier_name}.\n\n` + 'Al quitarle el repartidor, ese monto sale del reporte y el pedido ' + 'vuelve a la cola de pendientes. ¿Continuar?' : `¿Quitar a ${delivery.courier_name} del pedido #${order.order_number || order.id}?\n\nEl pedido vuelve a la cola de pendientes por asignar.`);
    if (!ok) return;
    setBusyOrderId(order.id);
    setError(null);
    setNotice(null);
    const result = await unassignExpressDelivery({
      restaurantId,
      orderId: order.id,
      deliveryId: delivery.id
    });
    setBusyOrderId(null);
    if (!result.success) {
      setError(result.error || 'No se pudo quitar el repartidor.');
      return;
    }
    // El próximo asignado necesita una llave nueva de idempotencia.
    keysRef.current.delete(order.id);
    setNotice('Repartidor removido. El pedido volvió a pendientes.');
    onChanged();
  };
  const handleTogglePaid = async (row: ExpressAssignmentRow) => {
    if (!row.delivery) return;
    const deliveryId = row.delivery.id;
    setBusyDeliveryId(deliveryId);
    setError(null);
    setNotice(null);
    const result = await setExpressDeliveryPaid({
      restaurantId,
      deliveryId,
      paid: !row.delivery.paid_at
    });
    setBusyDeliveryId(null);
    if (!result.success) {
      setError(result.error || 'No se pudo actualizar el pago.');
      return;
    }
    onChanged();
  };
  return <section aria-labelledby="express-assign-heading" className="bg-white rounded-2xl border border-gray-200" data-id="element-760" data-mp="10m95700fw65d">
      <header className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3" data-id="element-761" data-mp="vozdfdpkvied">
        <div className="flex items-center gap-2 min-w-0" data-id="element-762" data-mp="m2y8u8moe67h">
          <TruckIcon className="w-5 h-5 text-gray-700 shrink-0" data-id="element-763" data-mp="r0hlx57n0cxu" />
          <div className="min-w-0" data-id="element-764" data-mp="d06vu4e5j2dq">
            <h2 id="express-assign-heading" className="font-bold text-gray-900" data-id="element-765" data-mp="d18eov1est60">
              Asignar entregas
            </h2>
            <p className="text-xs text-gray-500" data-id="element-766" data-mp="h3lbuv23bir6">
              {rows.length} entrega{rows.length === 1 ? '' : 's'} a domicilio ·{' '}
              {pendingCount} sin repartidor
            </p>
          </div>
        </div>
        {canSeeMoney && pendingPayout > 0 && <div className="text-right" data-id="element-767" data-mp="vy9oct5wrhga">
            <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide" data-id="element-768" data-mp="100d1dm886u3o">
              Pendiente de pagar
            </p>
            <p className="text-lg font-black text-gray-900 tabular-nums" data-id="element-769" data-mp="1nppayn7gt46i">
              {formatCurrency(pendingPayout)}
            </p>
          </div>}
      </header>

      {error && <div role="alert" className="mx-5 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2" data-id="element-770" data-mp="3d6azad8b8gk3">
          <AlertTriangleIcon className="w-4 h-4 shrink-0 mt-0.5" data-id="element-771" data-mp="3mww62ny50re3" />
          <span data-id="element-772" data-mp="3mw4a3c0morna">{error}</span>
        </div>}
      {notice && <div className="mx-5 mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800" data-id="element-773" data-mp="3hyuefemnbjr0">
          {notice}
        </div>}

      {activeCouriers.length === 0 && !loading && rows.length > 0 && <div className="mx-5 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-start gap-2" data-id="element-774" data-mp="2payiic4whvva">
          <AlertTriangleIcon className="w-4 h-4 shrink-0 mt-0.5" data-id="element-775" data-mp="2z7l45351wqyd" />
          <span data-id="element-776" data-mp="2z9ebylrehh7y">
            No hay repartidores activos. Registrá al menos uno arriba para
            poder asignar estas entregas.
          </span>
        </div>}

      <div className="p-5" data-id="element-777" data-mp="egatkx6arg68">
        {loading ? <div className="flex justify-center py-12 text-gray-400" data-id="element-778" data-mp="27ztddehs6j56">
            <Loader2Icon className="w-6 h-6 animate-spin" data-id="element-779" data-mp="281miuqgnqxbl" />
          </div> : rows.length === 0 ? <div className="text-center py-12" data-id="element-780" data-mp="2cwtjtqijlnwt">
            <PackageIcon className="w-10 h-10 mx-auto mb-3 text-gray-300" data-id="element-781" data-mp="2hxpinpo1ejvd" />
            <p className="font-bold text-gray-900" data-id="element-782" data-mp="2mofp76878srn">
              No hubo entregas a domicilio en este periodo
            </p>
            <p className="text-sm text-gray-500 mt-1" data-id="element-783" data-mp="2rok9z8g549no">
              Probá ampliando el rango de fechas.
            </p>
          </div> : <ul className="space-y-2" data-id="element-784" data-mp="2vr8hnct4baj2">
            {rows.map(row => {
          const {
            order,
            delivery
          } = row;
          const isPaid = Boolean(delivery?.paid_at);
          const busy = busyOrderId === order.id;
          const mapsHref = order.maps_url || (order.delivery_lat && order.delivery_lng ? `https://www.google.com/maps/search/?api=1&query=${order.delivery_lat},${order.delivery_lng}` : null);
          return <li key={order.id} className={cn('p-3 rounded-xl border', isPaid ? 'border-gray-200 bg-gray-50' : delivery ? 'border-gray-200 bg-white' : 'border-amber-200 bg-amber-50/40')} data-id="element-785" data-mp="yzb6ytjbaon8">
                  <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-786" data-mp="1hpsv0kt78w29">
                    <div className="min-w-0 flex-1" data-id="element-787" data-mp="1hnyah7lub5yp">
                      <div className="flex items-center gap-2 flex-wrap" data-id="element-788" data-mp="1cpofoy4127dm">
                        <span className="text-xs font-black text-gray-900 tabular-nums" data-id="element-789" data-mp="17s4nsveepw37">
                          #{order.order_number || order.id}
                        </span>
                        <span className="text-xs text-gray-500 tabular-nums" data-id="element-790" data-mp="3shu3famxplwy">
                          {fmtTimeCR(order.created_at)}
                        </span>
                        {canSeeMoney && order.delivery_fee === 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-gray-200 text-gray-700" data-id="element-791" data-mp="ulqrk1zs0q8y">
                            Envío ₡0
                          </span>}
                        {canSeeMoney && isPaid && <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-700" data-id="element-792" data-mp="1ia9x49rfyoxc">
                            Pagado
                          </span>}
                      </div>
                      <p className="text-sm font-bold text-gray-900 truncate mt-0.5" data-id="element-793" data-mp="1s7ep4q7yxem2">
                        {order.customer_name || 'Sin nombre'}
                      </p>
                      {order.delivery_address && <p className="text-xs text-gray-500 flex items-start gap-1 mt-0.5" data-id="element-794" data-mp="3u02vv9jtmk6l">
                          <MapPinIcon className="w-3.5 h-3.5 shrink-0 mt-0.5" data-id="element-795" data-mp="3p2asnjdq7bn3" />
                          {mapsHref ? <a href={mapsHref} target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-900 break-words" data-id="element-796" data-mp="24l1kxwd5sa3n">
                              {order.delivery_address}
                            </a> : <span className="break-words" data-id="element-797" data-mp="1qral5fp8c4l4">
                              {order.delivery_address}
                            </span>}
                        </p>}
                    </div>

                    {canSeeMoney && <div className="text-right shrink-0" data-id="element-798" data-mp="1guxruixljsqw">
                        <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide" data-id="element-799" data-mp="369cbiotrcpfj">
                          A pagar
                        </p>
                        <p className="text-base font-black text-gray-900 tabular-nums" data-id="element-800" data-mp="39qhy4xa59x40">
                          {formatCurrency(delivery ? delivery.payout_amount : order.delivery_fee)}
                        </p>
                        {delivery && delivery.payout_amount !== order.delivery_fee && <p className="text-[10px] text-amber-700 font-medium" data-id="element-801" data-mp="3sfokcua5d6uk">
                              El envío del pedido cambió a{' '}
                              {formatCurrency(order.delivery_fee)}
                            </p>}
                      </div>}
                  </div>

                  <div className="flex flex-wrap items-center gap-2 mt-3" data-id="element-802" data-mp="f1bj2icykpmq">
                    <label htmlFor={`courier-for-${order.id}`} className="sr-only" data-id="element-803" data-mp="f3ei1rw39t3l">
                      Repartidor del pedido {order.order_number || order.id}
                    </label>
                    <select id={`courier-for-${order.id}`} value={delivery?.courier_id ?? ''} disabled={busy || activeCouriers.length === 0} onChange={e => handleCourierChange(row, e.target.value)} className="h-10 px-3 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-900 focus:outline-none focus:border-gray-900 disabled:opacity-40 min-w-[180px]" data-id="element-804" data-mp="1xxpruhg5mvbr">
                      {/* Elegir esta opción QUITA el repartidor y devuelve
                          el pedido a la cola de pendientes. */}
                      <option value="" data-id="element-805" data-mp="2pporbdsi8g95">
                        {delivery ? '— Quitar repartidor —' : 'Sin asignar'}
                      </option>
                      {/* El repartidor asignado puede estar desactivado:
                          se agrega para que el selector muestre su nombre
                          y no aparezca duplicado con la lista activa. */}
                      {delivery && !activeCouriers.some(c => c.id === delivery.courier_id) && <option value={delivery.courier_id} data-id="element-806" data-mp="2qr2sbf2imae9">
                            {delivery.courier_name} (inactivo)
                          </option>}
                      {activeCouriers.map(c => <option key={c.id} value={c.id} data-id="element-807" data-mp="1y3gp47x797rx">
                          {c.name}
                        </option>)}
                    </select>

                    {busy && <Loader2Icon className="w-4 h-4 animate-spin text-gray-400" data-id="element-808" data-mp="281wb5whddquk" />}

                    {delivery &&
              // Reenviable: abre WhatsApp con el mensaje ya
              // armado (cliente, dirección, pedido y monto a
              // cobrar solo si es efectivo). No guarda nada.
              <a href={buildCourierWhatsappHref(order, activeCouriers.find(c => c.id === delivery.courier_id)?.phone ?? null)} target="_blank" rel="noopener noreferrer" className="h-10 px-3 rounded-lg font-bold text-sm border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 active:scale-95 transition-all flex items-center gap-2" data-id="element-809" data-mp="3vy3y4029969u">
                        <MessageCircleIcon className="w-4 h-4" data-id="element-810" data-mp="8qs5hyi6qkep" />
                        Enviar por WhatsApp
                      </a>}

                    {canSeeMoney && delivery && <button onClick={() => handleTogglePaid(row)} disabled={busyDeliveryId === delivery.id} className={cn('h-10 px-3 rounded-lg font-bold text-sm border active:scale-95 transition-all disabled:opacity-40 flex items-center gap-2', isPaid ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400')} data-id="element-811" data-mp="11b0ewlswkm57">
                        {busyDeliveryId === delivery.id ? <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-812" data-mp="jei02fqph0tn" /> : isPaid ? <CheckCircle2Icon className="w-4 h-4" data-id="element-813" data-mp="eg6rs9g26ydj" /> : <BanknoteIcon className="w-4 h-4" data-id="element-814" data-mp="9f0xw8anx52f" />}
                        {isPaid ? 'Pagado' : 'Marcar pagado'}
                      </button>}

                    {canSeeMoney && delivery?.paid_at && <span className="text-xs text-gray-500 tabular-nums" data-id="element-815" data-mp="3vggl5ucntd6w">
                        {fmtTimeCR(delivery.paid_at)}
                      </span>}
                  </div>
                </li>;
        })}
          </ul>}
      </div>
    </section>;
}